/**
 * Barre d'état en bas de l'écran, façon jeu de gestion (Cities: Skylines) :
 * vitesse et horloge (jour, progression de la journée, damage de nuit),
 * blason de la station, ressources (trésorerie et tendance, visiteurs,
 * satisfaction, domaine, stocks) et météo avec les prévisions. Les infobulles
 * passent par l'attribut data-tip (game.css), plus lisibles que title.
 */
import type { ComponentChildren } from 'preact';
import {
  CLOSE_MINUTE,
  OPEN_MINUTE,
  calendarDay,
  dayCosts,
  dayIndex,
  dayRevenue,
  formatHour,
  minuteOfDay,
} from '@snowchain/shared';
import { hud } from './context';
import { euros, n0, n1 } from './format';
import { REGIME, levelColor } from './labels';
import { Stars } from './game-ui';
import { PgCoin, PgCrate, PgDrop, PgFrown, PgFuel, PgMeh, PgMountain, PgPeople, PgSmile, PgWind, type AnyIcon } from './icons';
import { cashTrendPerHour } from './trends';
import { ui, type OfficeTab } from './ui-state';
import { SpeedControl } from './speed';
import { GroomingStrip } from './grooming';

const openOffice = (tab: OfficeTab) => {
  if (hud.store?.isOwner) ui.office.value = ui.office.value === tab ? null : tab;
};

function StationCard() {
  const store = hud.store!;
  const r = store.replica!;
  const s = r.station.stats;
  const name = r.station.name;
  const initials = name
    .split(/[\s'-]+/)
    .filter((w) => w.length > 2)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const online = r.world.players.filter((p) => p.online);
  const ai = r.world.postes.filter((p) => p.controller === 'ai');
  const roster = [...online.map((p) => `${p.name} (${p.role === 'observer' ? 'observateur' : 'station'})`), ...ai.map((p) => `${p.name} (IA)`)].join('\n');
  const tip = `${store.isOwner ? 'Vous gérez cette station' : 'Vous observez cette station'}\nRéputation ${n0(s.reputation * 100)} % : moyenne glissante de la satisfaction des journées${roster ? `\n\n${roster}` : ''}`;
  return (
    <div class="g-station" data-tip={tip} data-tip-align="start">
      <div class="g-crest" aria-hidden="true">
        {initials || 'SC'}
      </div>
      <div class="g-station-text">
        <div class="g-station-name">{name}</div>
        <div class="g-station-sub">
          <Stars value={s.reputation * 5} />
          <span class="g-sub">
            {online.length} joueur{online.length > 1 ? 's' : ''} · {store.status === 'online' ? `${n0(store.rtt)} ms` : 'hors ligne'}
          </span>
        </div>
      </div>
    </div>
  );
}

type ResTone = 'money' | 'people' | 'ok' | 'mid' | 'bad' | 'domain';

function Resource(props: { icon: AnyIcon; tone: ResTone; label: string; value: string; unit?: string; sub?: ComponentChildren; onClick?: () => void; tip?: string; class?: string }) {
  const Icon = props.icon;
  return (
    <button type="button" class={`g-res g-res-${props.tone} ${props.class ?? ''}`} onClick={props.onClick} data-tip={props.tip ?? props.label} aria-label={props.label} disabled={!props.onClick}>
      <Icon size={22} class="g-res-ic" />
      <span class="g-res-text">
        <span class="g-res-value num">
          {props.value}
          {props.unit && <small>{props.unit}</small>}
        </span>
        <span class="g-res-sub">{props.sub ?? props.label}</span>
      </span>
    </button>
  );
}

/** Jauge verticale d'un stock, à la manière des barres de demande des jeux de gestion. */
function StockGauge(props: { icon: AnyIcon; tone: 'food' | 'fuel' | 'water'; label: string; qty: number; cap: number; unit: string; days: number | null }) {
  const ratio = props.cap ? Math.max(0, Math.min(1, props.qty / props.cap)) : 0;
  const level = ratio <= 0.08 ? 3 : ratio < 0.25 ? 2 : ratio < 0.4 ? 1 : 0;
  const Icon = props.icon;
  return (
    <span class={`g-stock g-stock-${props.tone}`} data-tip={`${props.label} : ${n0(props.qty)} ${props.unit} sur ${n0(props.cap)}${props.days !== null ? `\nEnviron ${n1(props.days)} j au rythme actuel` : ''}`}>
      <span class="g-stock-bar">
        <i style={{ height: `${Math.max(6, ratio * 100)}%`, background: levelColor(level) }} />
      </span>
      <Icon size={13} />
    </span>
  );
}

function ResourceBar() {
  const store = hud.store!;
  const r = store.replica!;
  const s = r.station.stats;
  const me = r.me;
  const game = store.clock.gameNow();
  const t = me?.today;
  const result = t ? dayRevenue(t) - dayCosts(t) : 0;
  const trend = me ? cashTrendPerHour(game, me.cash) : null;
  const sat = s.satisfaction;
  const SatIcon = sat >= 70 ? PgSmile : sat >= 50 ? PgMeh : PgFrown;
  const crowd = s.parkingCapacity ? s.onDomain / s.parkingCapacity : 0;
  const missed = s.missed;
  const refused = missed ? missed.parking + missed.tickets : s.refusedToday;
  const openLifts = [...r.lifts.values()].filter((l) => l.status !== 'construction').length;
  const stock = (k: 'food' | 'fuel' | 'water') => me?.stocks[k];
  const days = (k: 'food' | 'fuel') => {
    const st = stock(k);
    if (!st) return null;
    const daily = st.dailyUse || (k === 'food' ? s.onDomain * 0.6 : 60);
    return daily > 0 ? st.qty / daily : null;
  };
  const crowdLevel = crowd >= 0.97 ? 3 : crowd >= 0.9 ? 2 : crowd >= 0.8 ? 1 : 0;
  return (
    <div class="g-resources">
      {me && (
        <Resource
          icon={PgCoin}
          tone="money"
          label="Trésorerie"
          value={euros(me.cash, true)}
          onClick={() => openOffice('finances')}
          tip={`Trésorerie : ${euros(me.cash)}\nRésultat du jour : ${result >= 0 ? '+' : ''}${euros(result)}\nFinances (G)`}
          sub={
            trend !== null ? (
              <span class={trend >= 0 ? 'g-up' : 'g-down'}>
                <i class={`g-trend ${trend >= 0 ? 'up' : 'down'}`} />
                {trend >= 0 ? '+' : ''}
                {euros(trend, true)}/h
              </span>
            ) : (
              <span class={result >= 0 ? 'g-up' : 'g-down'}>
                jour {result >= 0 ? '+' : ''}
                {euros(result, true)}
              </span>
            )
          }
        />
      )}
      <Resource
        icon={PgPeople}
        tone="people"
        label="Visiteurs"
        value={n0(s.onDomain)}
        unit={`/ ${n0(s.parkingCapacity)}`}
        onClick={store.isOwner ? () => openOffice('frequentation') : undefined}
        tip={`Visiteurs sur le domaine : ${n0(s.onDomain)} pour ${n0(s.parkingCapacity)} places\n${n0(s.visitorsToday)} forfaits vendus aujourd'hui, ${n0(refused)} visiteurs refusés\nFréquentation (F)`}
        sub={
          <span class="g-res-bar">
            <span class="g-lbar">
              <span style={{ width: `${Math.max(2, Math.min(1, crowd) * 100)}%`, background: levelColor(crowdLevel) }} />
            </span>
            {refused > 0 && <span class="g-down">{n0(refused)} refusés</span>}
          </span>
        }
      />
      <Resource
        icon={SatIcon}
        tone={sat >= 70 ? 'ok' : sat >= 50 ? 'mid' : 'bad'}
        label="Satisfaction"
        value={n0(sat)}
        unit="%"
        tip="Satisfaction moyenne des visiteurs présents : files, prix, neige, repas"
        sub={<span class="g-res-muted">satisfaction</span>}
      />
      <Resource
        icon={PgMountain}
        tone="domain"
        label="Domaine"
        value={n1(s.openKm)}
        unit="km"
        tip="Pistes ouvertes et remontées en service"
        sub={
          <span class="g-res-muted">
            {s.openLifts}/{openLifts} RM · {s.openPistes} pistes
          </span>
        }
      />
      {me && (
        <button type="button" class="g-res g-res-stocks" onClick={() => openOffice('stocks')} aria-label="Stocks et commandes">
          <span class="g-stocks">
            <StockGauge icon={PgCrate} tone="food" label="Denrées" qty={me.stocks.food.qty} cap={me.stocks.food.capacity} unit="repas" days={days('food')} />
            <StockGauge icon={PgFuel} tone="fuel" label="Carburant" qty={me.stocks.fuel.qty} cap={me.stocks.fuel.capacity} unit="L" days={days('fuel')} />
            <StockGauge icon={PgDrop} tone="water" label="Retenue d'eau" qty={me.stocks.water.qty} cap={me.stocks.water.capacity} unit="m³" days={null} />
          </span>
        </button>
      )}
    </div>
  );
}

function ClockCard() {
  const store = hud.store!;
  const r = store.replica!;
  const game = store.clock.gameNow();
  const minute = minuteOfDay(game);
  const cal = calendarDay(dayIndex(game));
  const host = r.world.hostId === store.welcome?.playerId;
  const night = minute < OPEN_MINUTE - 60 || minute >= CLOSE_MINUTE + 60;
  const progress = Math.max(0, Math.min(1, (minute - OPEN_MINUTE) / (CLOSE_MINUTE - OPEN_MINUTE)));
  const phase = minute < OPEN_MINUTE ? `Ouverture à ${formatHour(OPEN_MINUTE)}` : minute >= CLOSE_MINUTE ? 'Remontées fermées' : `Fermeture à ${formatHour(CLOSE_MINUTE)}`;
  const h = Math.floor(minute / 60);
  const mm = String(Math.floor(minute % 60)).padStart(2, '0');
  return (
    <div class={`g-clock${night ? ' g-night' : ''}`}>
      <SpeedControl host={host} />
      <div class="g-clock-main" data-tip={`${phase}\nJour ${dayIndex(game)} · ${cal.label}${cal.holiday ? ` · ${cal.holiday}` : cal.weekend ? ' · week-end' : ''}`} data-tip-align="start">
        <span class="g-clock-time num">
          {h}:{mm}
        </span>
        <span class="g-clock-day">
          <b>Jour {dayIndex(game)}</b> {cal.label}
          {(cal.holiday || cal.weekend) && <span class={`g-daytag${cal.holiday ? ' g-daytag-holiday' : ''}`}>{cal.holiday ?? 'Week-end'}</span>}
        </span>
        <span class="g-dayline">
          <span class="g-dayline-fill" style={{ width: `${progress * 100}%` }} />
          <span class="g-dayline-sun" style={{ left: `${progress * 100}%` }} />
        </span>
      </div>
      <GroomingStrip />
    </div>
  );
}

function WeatherCard() {
  const w = hud.store!.replica!.world.weather;
  const reg = REGIME[w.regime];
  const temp = `${n1(w.temp).replace('-', '\u2212')} °C`;
  const days = w.forecast.map((f) => `${calendarDay(f.day).label} : ${REGIME[f.regime].label.toLowerCase()}, ${f.tMin} à ${f.tMax} °C${f.snowCm > 0 ? `, ${f.snowCm} cm` : ''}, vent ${f.windMax} km/h`);
  return (
    <div class="g-weather" data-tip={`${reg.label}, ${temp}, vent ${n0(w.wind)} km/h\n${n0(w.snowNext24h)} cm de neige prévus sur 24 h${days.length ? `\n\n${days.join('\n')}` : ''}`} data-tip-align="end">
      <reg.icon size={24} class={`g-wx g-wx-${w.regime}`} />
      <span class="g-res-text">
        <span class="g-res-value num">{temp}</span>
        <span class="g-res-sub">
          <PgWind size={11} /> {n0(w.wind)} km/h{w.snowNext24h > 0 ? ` · ${n0(w.snowNext24h)} cm` : ''}
        </span>
      </span>
      <span class="g-forecast">
        {w.forecast.map((f) => {
          const R = REGIME[f.regime];
          return (
            <span key={f.day} class="g-fc">
              <R.icon size={15} class={`g-wx g-wx-${f.regime}`} />
              <small>{calendarDay(f.day).label.split(' ')[0]}</small>
            </span>
          );
        })}
      </span>
    </div>
  );
}

export function TopBar() {
  void ui.tick.value;
  const store = hud.store;
  if (!store?.replica) return null;
  return (
    <div class="g-top g-statusbar">
      <ClockCard />
      <StationCard />
      <ResourceBar />
      <WeatherCard />
    </div>
  );
}
